import { Grid } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { CertificationFormVM } from 'civil-status/view-models/document-birth-form';
import { useFormikContext } from 'formik';
import { memo, useMemo } from 'react';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { noop } from 'shell/utils/noop';
import { CertificationStep } from 'civil-status/constants/certification-step';
import {
  initialApplicant,
  intialAccountholder,
} from 'shell/components/shared/certification-civil-status';
import OtpButton from 'civil-status/components/shared/otp-button';

const steps: Array<Record<string, string>> = [
  {
    label: 'Richiedente e intestatario',
  },
  {
    label: 'Lista atti',
  },
  {
    label: 'Visualizzazione atto',
  },
  {
    label: 'Scelta certificazione',
  },
];

interface ButtonsProps {
  handleExit: () => void;
  disabled?: boolean;
  handleStep?: (id: number, resolve?: (value: unknown) => void) => void;
  activeStep?: number;
}

const ButtonsComponent = ({
  handleExit,
  disabled,
  handleStep = noop,
  activeStep = CertificationStep.ApplicantAccountholder,
}: ButtonsProps): React.ReactElement => {
  const formContext = useFormikContext<CertificationFormVM>();

  const isDisabledRequestCertificate = useMemo(() => {
    if (!formContext.errors || Object.values(formContext.errors).length === 0) {
      return '';
    }
    if (activeStep === CertificationStep.ApplicantAccountholder) {
      return 'Il form del Richiedente non è valido';
    }
    if (activeStep === CertificationStep.ChooseCertificate) {
      return formContext.errors.certificate;
    }
    if (activeStep === CertificationStep.Search) {
      return "Il form dell'atto non è valido";
    }
    if (activeStep === CertificationStep.Visualize) {
      return "Il form dell'atto non è valido";
    }
    if (activeStep === CertificationStep.Print) {
      return 'Il form della stampa non è valido';
    }
  }, [formContext.errors, activeStep]);

  return (
    <StickyDiv>
      <div>
        <Grid container spacing={1}>
          <OtpButton isWithStyleStepButton />
          {steps.map((step, index) => (
            <Grid item xs={12} key={step.label?.toString()}>
              <PmiTooltipError
                message={isDisabledRequestCertificate}
                show={!!isDisabledRequestCertificate && activeStep === index}
              >
                <span>
                  <StepButton
                    active={index === activeStep}
                    disabled={disabled || activeStep < index}
                    variant="outlined"
                    color="cdmBrandThird"
                    fullWidth
                    onClick={async (event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (index === 2) {
                        formContext.setFieldValue('chosenDocument', null);
                        await Promise.resolve();
                      }
                      handleStep(index);
                    }}
                  >
                    <StepTypography
                      active={index === activeStep}
                      disabled={disabled || activeStep < index}
                    >
                      {step.label}
                    </StepTypography>
                  </StepButton>
                </span>
              </PmiTooltipError>
            </Grid>
          ))}
        </Grid>
        {activeStep === CertificationStep.ApplicantAccountholder ? (
          <div className="pmi-pt-3">
            <div className="pmi-pb-2">
              <StepButton
                variant="outlined"
                color="cdmBrandThird"
                fullWidth
                onClick={async (event) => {
                  event.stopPropagation();
                  event.preventDefault();

                  formContext.setValues({
                    ...formContext.values,
                    applicant: initialApplicant, // reset richiedente
                    accountholder: {
                      ...intialAccountholder,
                      sameAsApplicant: formContext.values.accountholder?.sameAsApplicant,
                    }, // reset intestatario
                  });
                }}
              >
                <StepTypography>Pulisci</StepTypography>
              </StepButton>
            </div>
            <div className="pmi-pb-2">
              <StepButton
                variant="outlined"
                color="cdmBrandThird"
                fullWidth
                onClick={async (event) => {
                  event.stopPropagation();
                  event.preventDefault();

                  formContext.setFieldValue('accountholder', {
                    ...intialAccountholder,
                    sameAsApplicant: formContext.values.accountholder?.sameAsApplicant,
                  }); // reset intestatario
                }}
              >
                <StepTypography>Pulisci solo intestatario</StepTypography>
              </StepButton>
            </div>
          </div>
        ) : undefined}
        <div>
          <div className="pmi-pt-3">
            <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={handleExit}>
              Esci
            </PmiButton>
          </div>
        </div>
      </div>
    </StickyDiv>
  );
};

ButtonsComponent.displayName = 'Certification Civil Status Form - Buttons';

const Buttons = memo(ButtonsComponent);

export default Buttons;
